import React, { useState, useEffect, useRef } from "react";
import { Box } from "@mui/material";
import Header from "../components/Header";
import ByDate, { ByDateRef } from "./ByDate";
import ByJobCode, { ByJobCodeRef } from "./ByJobCode";
import ByTech, { ByTechRef } from "./ByTech";
import ByServiceOrder, { ByServiceOrderRef } from "./ByServiceOrder";
import { useFilter } from "../context/FilterContext";
import ByAppliance, { ByApplianceRef } from "./ByAppliance";
import EstimateOptimization, { EstimateOptimizationRef } from "./EstimateOptimization";
import AlgorithmDocumentation from "./AlgorithmDocumentation";

const Home: React.FC = () => {
  // Use FilterContext instead of local state
  const { activeTab, setActiveTab, getFilterState, updateFilterState } = useFilter();
  const [lastUpdated, setLastUpdated] = useState<string>("N/A");

  // Refs for components that support refresh
  const byDateRef = useRef<ByDateRef>(null);
  const byJobCodeRef = useRef<ByJobCodeRef>(null);
  const byTechRef = useRef<ByTechRef>(null);
  const byServiceOrderRef = useRef<ByServiceOrderRef>(null);
  const byApplianceRef = useRef<ByApplianceRef>(null);
  const estimateOptimizationRef = useRef<EstimateOptimizationRef>(null);

  // Fetch last updated date from API on component mount
  useEffect(() => {
    const fetchLastUpdated = async () => {
      try {
        const response = await fetch("/api/snowflake/last-updated");
        const data = await response.json();
        if (data.success && data.lastUpdated) {
          setLastUpdated(data.lastUpdated);
        }
      } catch (error) {
        console.error("Error fetching last updated date:", error);
      }
    };

    fetchLastUpdated();
  }, []);

  // Simplified filter change handler using context
  const handleFilterChange = (
    isRefresh: boolean,
    startDate: string | null,
    endDate: string | null,
    filter: string | null,
    pageNum?: number,
    limitNum?: number,
    quotedJobcode?: string | null,
    techLdap?: string | null,
    acctgDt?: string | null,
    declineReason?: string | null,
    applianceType?: string | null,
    createSource?: string | null,
    llmChatClassification?: string | null,
    orderNumber?: string | null,
    customerRating?: string | null
  ) => {
    const currentState = getFilterState(activeTab);
    updateFilterState(activeTab, {
      startDate: startDate !== undefined ? startDate : currentState.startDate,
      endDate: endDate !== undefined ? endDate : currentState.endDate,
      filter: filter !== null ? filter : "",
      quotedJobcode:
        quotedJobcode !== undefined && quotedJobcode !== null
          ? quotedJobcode
          : currentState.quotedJobcode,
      techLdap:
        techLdap !== undefined && techLdap !== null ? techLdap : currentState.techLdap,
      acctgDt:
        acctgDt !== undefined && acctgDt !== null ? acctgDt : currentState.acctgDt,
      declineReason:
        declineReason !== undefined && declineReason !== null
          ? declineReason
          : currentState.declineReason,
      applianceType:
        applianceType !== undefined && applianceType !== null
          ? applianceType
          : currentState.applianceType,
      createSource:
        createSource !== undefined && createSource !== null
          ? createSource
          : currentState.createSource,
      llmChatClassification:
        llmChatClassification !== undefined && llmChatClassification !== null
          ? llmChatClassification
          : currentState.llmChatClassification,
      orderNumber:
        orderNumber !== undefined && orderNumber !== null
          ? orderNumber
          : currentState.orderNumber,
      customerRating:
        customerRating !== undefined && customerRating !== null
          ? customerRating
          : currentState.customerRating,
      page: pageNum !== undefined ? pageNum : currentState.page,
      limit: limitNum !== undefined ? limitNum : currentState.limit,
    });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleRefresh = () => {
    switch (activeTab) {
      case 0: // By Date
        byDateRef.current?.refresh();
        break;
      case 1: // By JobCode
        byJobCodeRef.current?.refresh();
        break;
      case 2: // By Appliance
        byApplianceRef.current?.refresh();
        break;
      case 3: // By Tech
        byTechRef.current?.refresh();
        break;
      case 4: // By Service Order
        byServiceOrderRef.current?.refresh();
        break;
      case 5: // Estimate Optimization
        estimateOptimizationRef.current?.refresh();
        break;
      case 6: // Estimate Optimization Doc
        // No refresh needed for documentation
        break;
      default:
        // For other tabs, we don't have refresh functionality yet
        console.log(`Refresh not implemented for tab ${activeTab}`);
        break;
    }
  };

  const renderTabContent = () => {
    const filterState = getFilterState(activeTab);
    
    switch (activeTab) {
      case 0:
        return (
          <ByDate
            ref={byDateRef}
            filterState={filterState}
            onFilterChange={handleFilterChange}
          />
        );
      case 1:
        return (
          <ByJobCode
            ref={byJobCodeRef}
            filterState={filterState}
            onFilterChange={handleFilterChange}
          />
        );
      case 2:
        return (
          <ByAppliance
            ref={byApplianceRef}
            filterState={filterState}
            onFilterChange={handleFilterChange}
          />
        );
      case 3:
        return (
          <ByTech
            ref={byTechRef}
            filterState={filterState}
            onFilterChange={handleFilterChange}
          />
        );
      case 4:
        return (
          <ByServiceOrder
            ref={byServiceOrderRef}
            filterState={filterState}
            onFilterChange={handleFilterChange}
          />
        );
      case 5:
        return (
          <EstimateOptimization
            ref={estimateOptimizationRef}
            filterState={filterState}
            onFilterChange={handleFilterChange}
          />
        );
      case 6:
        return <AlgorithmDocumentation />;
      default:
        return (
          <ByDate
            ref={byDateRef}
            filterState={filterState}
            onFilterChange={handleFilterChange}
          />
        );
    }
  };

  return (
    <Box>
      <Header
        activeTab={activeTab}
        onTabChange={handleTabChange}
        lastUpdated={lastUpdated}
        onRefresh={handleRefresh}
      />
      {renderTabContent()}
    </Box>
  );
};

export default Home;
